import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetMyInvestmentDetailsQuery } from '@app/api/investmentsApi';
import DocumentIcon from '@app/components/icons/DocumentIcon';
import { PageLoader } from '@app/components/PageLoader';
import { DATE_FORMAT } from '@app/configs/dateTime';
import { customDocumentsList, getDocumentsInvestor } from '@app/configs/documentsLists';
import { ROUTES } from '@app/configs/routes';
import { getUserLocale } from '@app/utils/getUserLocale';
import { getLocalDateTime } from '@app/utils/time';
import CustomDocuments from '@modules/borrower/components/loanDetails/CustomDocuments';
import formatAmount from '@modules/calculator/utils/formatAmount';
import { TAB_KEYS } from '@modules/investor/config/tabs';
import MainDetails from './components/MainDetails';
const MyInvestmentDetails = ({ isStandalone }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const locale = getUserLocale();
    const { data: selectedInvestment, isLoading: loadingDetails, error: detailsError, } = useGetMyInvestmentDetailsQuery(Number(id), {
        skip: !id,
    });
    if (loadingDetails && !detailsError) {
        return <PageLoader />;
    }
    if (detailsError) {
        return <Navigate to={`${isStandalone ? ROUTES.investorStart : ROUTES.home}?tab=${TAB_KEYS.MY_INVESTMENTS}`}/>;
    }
    const myInvestment = selectedInvestment === null || selectedInvestment === void 0 ? void 0 : selectedInvestment.myInvestments[0];
    const documents = getDocumentsInvestor(locale);
    const sidebarPairs = [
        myInvestment && {
            label: t('financing.investment_details.invested_amount'),
            value: formatAmount(myInvestment.investedAmount),
        },
        {
            label: t('financing.investment_details.interest_rate'),
            value: selectedInvestment ? `${selectedInvestment.annualRate}%` : '',
        },
        {
            label: t('financing.investment_details.investors'),
            value: selectedInvestment ? String(selectedInvestment.investmentCount) : '',
        },
        {
            label: t('financing.investment_details.account'),
            value: selectedInvestment ? selectedInvestment.walletNumber : '',
        },
        myInvestment && {
            label: t('financing.investment_details.investment_date'),
            value: getLocalDateTime(myInvestment.investmentDate, DATE_FORMAT.dateTime),
        },
        myInvestment && {
            label: t('financing.investment_details.agreement_start_date'),
            value: getLocalDateTime(myInvestment.agreementStartDate, DATE_FORMAT.dateTime),
        },
    ].filter(Boolean);
    return (<div className="flex flex-col gap-6 w-full">
            <h1 className="headline-m text-neutral-primary-initial">{t('financing.breadcrumbs.investment_details')}</h1>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 w-full">
                <div className="flex flex-col gap-6">
                    <MainDetails details={selectedInvestment}/>
                    <div className="flex flex-col gap-4">
                        <h2 className="title-l text-neutral-primary-initial">
                            {t('financing.loan_details.documents.title')}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                            {documents.map((doc) => (<a key={doc.name} href={doc.url} target="_blank" rel="noreferrer" className="title-s text-action-primary-initial flex items-center gap-2">
                                    <DocumentIcon className="size-4 shrink-0"/>
                                    <span>{t(doc.name)}</span>
                                </a>))}
                            <CustomDocuments documents={customDocumentsList.investor}/>
                        </div>
                    </div>
                </div>
                <aside className="flex flex-col gap-4 p-6 rounded-lg bg-surface-neutral-secondary-initial h-fit">
                    <h2 className="title-m text-neutral-primary-initial">
                        {t('financing.investment_details.investment')}
                    </h2>
                    <p className="headline-s text-neutral-primary-initial pb-4 border-b border-neutral-primary-initial">
                        {myInvestment ? formatAmount(myInvestment.investedAmount) : ''}
                    </p>
                    <div className="flex flex-col gap-2">
                        {sidebarPairs.map((item) => (<div key={item.label} className="flex justify-between gap-3">
                                <span className="label-s text-neutral-secondary-initial">{item.label}:</span>
                                <span className="title-s text-neutral-primary-initial text-right">{item.value}</span>
                            </div>))}
                    </div>
                </aside>
            </div>
        </div>);
};
export default MyInvestmentDetails;
